import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
	DataGrid,
	GridColDef,
	GridFilterModel,
	GridRenderCellParams,
	GridSortModel,
} from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';
import {
	ADMIN_SYMBOL_PAGE_SIZE_OPTIONS,
	AdminSymbolPageSize,
	AdminSymbolSortField,
	IAdminSymbolDataset,
	IAdminSymbol,
	IAdminSymbolFilters,
	IAdminSymbolSort,
	isAdminSymbolPageSize,
	useAdminSymbolDatasetsQuery,
	useAdminSymbolsQuery,
} from '../../api/admin';
import AdminDatasetSelector from './AdminDatasetSelector';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';
import SymbolDetailsDialog from './SymbolDetailsDialog';
import {
	SymbolsCompanyColumnFilter,
	SymbolsDateColumnFilter,
	SymbolsDsTypeColumnFilter,
	SymbolsMappingStatusColumnFilter,
} from './SymbolsColumnFilter';
import SymbolsTableViews, { SymbolsTableViewState } from './SymbolsTableViews';
import { parsePositiveIdList } from '../../utils/searchParams';

const INITIAL_PAGE_SIZE: AdminSymbolPageSize = 50;
const MAX_VISIBLE_METADATA_CHIPS = 4;
const EMPTY_VALUE = '';
const formatSymbolTotal = (count: number) => count > 10000 ? '10,000+' : count.toLocaleString();
const DATASETS_PARAM = 'symbols_datasets';
const EMPTY_DATASETS: IAdminSymbolDataset[] = [];

type SymbolColumnFilters = Omit<IAdminSymbolFilters, 'datasetIds'>;

const INITIAL_COLUMN_FILTERS: SymbolColumnFilters = {
	firstSeenFrom: '',
	firstSeenTo: '',
	firstSeenMode: 'include',
	lastSeenFrom: '',
	lastSeenTo: '',
	lastSeenMode: 'include',
	companyName: '',
	companyNameMode: 'include',
	mappingStatus: 'any',
	dsTypes: [],
};

function formatNullableValue(value: unknown) {
	return value == null ? EMPTY_VALUE : value;
}

function parseNullableDate(value: unknown) {
	if (value == null) return null;
	if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

	const stringValue = String(value);
	const dateParts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(stringValue);
	if (dateParts) {
		const [, year, month, day] = dateParts;
		return new Date(Number(year), Number(month) - 1, Number(day));
	}

	const date = new Date(stringValue);
	return isNaN(date.getTime()) ? null : date;
}

function formatNullableDate(value: unknown) {
	if (value == null) return EMPTY_VALUE;
	const date = parseNullableDate(value);
	return date === null ? String(value) : date.toLocaleDateString('en-CA');
}

function formatMappingState(symbol: Pick<IAdminSymbol, 'company_id' | 'is_active'>) {
	if (symbol.company_id == null) return 'Unmapped';
	return symbol.is_active ? 'Active' : 'Inactive';
}

function formatMappedCompany(symbol: IAdminSymbol) {
	if (symbol.company_id == null) return EMPTY_VALUE;
	const mappedSymbol = symbol.company_symbol?.trim() || symbol.company_bloomberg_ticker?.trim();
	const companyName = symbol.company_name?.trim();
	const primary = mappedSymbol && companyName
		? `${mappedSymbol} | ${companyName}`
		: mappedSymbol || companyName || String(symbol.company_id);
	const parentName = symbol.parent_company_name?.trim();
	const parent = symbol.parent_company_id != null && parentName
		? ` (${parentName})`
		: '';
	return `${primary}${parent}`;
}

function getMappingChipColor(value: unknown): 'success' | 'error' | 'default' {
	if (value === 'Active') return 'success';
	if (value === 'Inactive') return 'error';
	return 'default';
}



function summarizeMetadataValue(value: unknown): string {
	if (value === null) return 'null';
	if (Array.isArray(value)) return `[${value.length} item${value.length === 1 ? '' : 's'}]`;
	if (typeof value === 'object') {
		const keys = Object.keys(value as Record<string, unknown>);
		return `{${keys.length} key${keys.length === 1 ? '' : 's'}}`;
	}
	return String(value);
}

function MetadataCell({ metadata }: { metadata: Record<string, unknown> | null }) {
	const entries = metadata ? Object.entries(metadata) : [];
	if (entries.length === 0) {
		return null;
	}

	const visibleEntries = entries.slice(0, MAX_VISIBLE_METADATA_CHIPS);
	const hiddenCount = entries.length - visibleEntries.length;
	const prettyMetadata = JSON.stringify(metadata, null, 2);

	return (
		<Tooltip
			title={(
				<Typography
					component="pre"
					variant="caption"
					sx={{
						m: 0,
						maxWidth: '70vw',
						maxHeight: '60vh',
						overflow: 'auto',
						whiteSpace: 'pre-wrap',
						overflowWrap: 'anywhere',
					}}
				>
					{prettyMetadata}
				</Typography>
			)}
			componentsProps={{ tooltip: { sx: { maxWidth: 'none' } } }}
		>
			<Box
				tabIndex={0}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 0.5,
					minWidth: 0,
					maxWidth: '100%',
					overflow: 'hidden',
					'&:focus-visible': {
						outline: 'auto',
						outlineColor: 'primary.main',
					},
				}}
			>
				{visibleEntries.map(([key, value]) => (
					<Chip
						key={key}
						label={`${key}: ${summarizeMetadataValue(value)}`}
						size="small"
						variant="outlined"
						sx={{
							maxWidth: '16rem',
							flexShrink: 1,
							'& .MuiChip-label': {
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							},
						}}
					/>
				))}
				{hiddenCount > 0 && (
					<Chip
						label={`+${hiddenCount}`}
						size="small"
						color="primary"
						variant="outlined"
						sx={{ flexShrink: 0 }}
					/>
				)}
			</Box>
		</Tooltip>
	);
}

function SymbolsNoRowsOverlay() {
	return (
		<Stack height="100%" alignItems="center" justifyContent="center" spacing={0.5}>
			<Typography variant="body2">No symbols found</Typography>
			<Typography variant="caption" color="text.secondary">
				Adjust the search to find a different symbol or mapping.
			</Typography>
		</Stack>
	);
}

function SymbolsDatasetSelectionOverlay() {
	return (
		<Stack height="100%" alignItems="center" justifyContent="center" spacing={0.5}>
			<Typography variant="body2">Select datasets to load symbols</Typography>
			<Typography variant="caption" color="text.secondary">
				Choose one or more datasets above.
			</Typography>
		</Stack>
	);
}

function SymbolsErrorOverlay() {
	return (
		<Stack height="100%" alignItems="center" justifyContent="center" spacing={0.5}>
			<Typography variant="body2" color="error.main">Symbols could not be loaded</Typography>
			<Typography variant="caption" color="text.secondary">
				Try again in a moment.
			</Typography>
		</Stack>
	);
}

interface CreateColumnsOptions {
	onOpen: (symbol: IAdminSymbol) => void;
	filters: SymbolColumnFilters;
	dsTypeOptions: string[];
	onFiltersChange: (filters: SymbolColumnFilters) => void;
}

function createColumns({
	onOpen,
	filters,
	dsTypeOptions,
	onFiltersChange,
}: CreateColumnsOptions): GridColDef<IAdminSymbol>[] {
	return [
		{
			field: 'is_active',
			headerName: 'Mapping Status',
			type: 'string',
			align: 'left',
			headerAlign: 'left',
			width: 180,
			minWidth: 170,
			disableColumnMenu: true,
			renderHeader: () => (
				<SymbolsMappingStatusColumnFilter
					label="Mapping Status"
					value={filters.mappingStatus}
					onApply={(mappingStatus) => onFiltersChange({ ...filters, mappingStatus })}
				/>
			),
			valueGetter: (params) => formatMappingState(params.row),
			renderCell: (params) => (
				<Chip
					label={params.value}
					color={getMappingChipColor(params.value)}
					size="small"
				/>
			),
		},
		{
			field: 'dataset_symbol_id',
			headerName: 'Dataset Symbol ID',
			type: 'number',
			align: 'left',
			headerAlign: 'left',
			width: 150,
			minWidth: 150,
			renderCell: (params) => (
				<Button
					variant="text"
					size="small"
					aria-label={`View details for dataset symbol ${params.value}`}
					onClick={(event) => {
						event.stopPropagation();
						onOpen(params.row);
					}}
					sx={{ minWidth: 0, px: 0.5, fontWeight: 700 }}
				>
					{params.value}
				</Button>
			),
		},
		{
			field: 'dataset_name',
			headerName: 'Dataset',
			width: 220,
			minWidth: 180,
			renderCell: (params) => `${params.row.dataset_name} (${params.row.dataset_id})`,
		},
		{
			field: 'ds_type',
			headerName: 'DS Type',
			width: 150,
			minWidth: 130,
			disableColumnMenu: true,
			renderHeader: () => (
				<SymbolsDsTypeColumnFilter
					label="DS Type"
					value={filters.dsTypes}
					options={dsTypeOptions}
					onApply={(dsTypes) => onFiltersChange({ ...filters, dsTypes })}
				/>
			),
			valueFormatter: ({ value }) => formatNullableValue(value),
		},
		{
			field: 'ds_symbol_1',
			headerName: 'Symbol 1',
			width: 170,
			minWidth: 140,
			valueFormatter: ({ value }) => formatNullableValue(value),
		},
		{
			field: 'ds_symbol_2',
			headerName: 'Symbol 2',
			width: 170,
			minWidth: 140,
			valueFormatter: ({ value }) => formatNullableValue(value),
		},
		{
			field: 'symbol_metadata',
			headerName: 'Metadata',
			width: 520,
			minWidth: 360,
			valueFormatter: ({ value }) => value ? JSON.stringify(value) : EMPTY_VALUE,
			renderCell: (params: GridRenderCellParams<Record<string, unknown> | null, IAdminSymbol>) => (
				<MetadataCell metadata={params.value ?? null} />
			),
		},
		{
			field: 'first_seen',
			headerName: 'First Seen',
			type: 'date',
			width: 170,
			minWidth: 150,
			renderHeader: () => (
				<SymbolsDateColumnFilter
					label="First Seen"
					value={{
						from: filters.firstSeenFrom,
						to: filters.firstSeenTo,
						mode: filters.firstSeenMode,
					}}
					onApply={(value) => onFiltersChange({
						...filters,
						firstSeenFrom: value.from,
						firstSeenTo: value.to,
						firstSeenMode: value.mode,
					})}
				/>
			),
			valueGetter: ({ value }) => parseNullableDate(value),
			valueFormatter: ({ value }) => formatNullableDate(value),
		},
		{
			field: 'last_seen',
			headerName: 'Last Seen',
			type: 'date',
			width: 170,
			minWidth: 150,
			renderHeader: () => (
				<SymbolsDateColumnFilter
					label="Last Seen"
					value={{
						from: filters.lastSeenFrom,
						to: filters.lastSeenTo,
						mode: filters.lastSeenMode,
					}}
					onApply={(value) => onFiltersChange({
						...filters,
						lastSeenFrom: value.from,
						lastSeenTo: value.to,
						lastSeenMode: value.mode,
					})}
				/>
			),
			valueGetter: ({ value }) => parseNullableDate(value),
			valueFormatter: ({ value }) => formatNullableDate(value),
		},
		{
			field: 'company_name',
			headerName: 'Mapped Company',
			width: 280,
			minWidth: 220,
			renderHeader: () => (
				<SymbolsCompanyColumnFilter
					label="Mapped Company"
					value={{
						value: filters.companyName,
						mode: filters.companyNameMode,
					}}
					onApply={(value) => onFiltersChange({
						...filters,
						companyName: value.value,
						companyNameMode: value.mode,
					})}
				/>
			),
			renderCell: (params) => formatMappedCompany(params.row),
		},
		{
			field: 'start_date',
			headerName: 'Mapped Start',
			type: 'date',
			width: 130,
			minWidth: 120,
			valueGetter: ({ value }) => parseNullableDate(value),
			valueFormatter: ({ value }) => formatNullableDate(value),
		},
		{
			field: 'end_date',
			headerName: 'Mapped End',
			type: 'date',
			width: 130,
			minWidth: 120,
			valueGetter: ({ value }) => parseNullableDate(value),
			valueFormatter: ({ value }) => formatNullableDate(value),
		},
	];
}

function parseSymbolSearch(input: string) {
	const search = input.trim();
	return search.length >= 2 ? [search] : [];
}

function getQuickFilterSearch(model: GridFilterModel) {
	const search = model.quickFilterValues?.[0];
	if (search === undefined) return '';
	const trimmedSearch = String(search).trim();
	return trimmedSearch.length >= 2 ? trimmedSearch : '';
}

function getSort(sortModel: GridSortModel): IAdminSymbolSort | null {
	const item = sortModel[0];
	if (!item?.sort) return null;
	return {
		field: item.field as AdminSymbolSortField,
		direction: item.sort,
	};
}

export default function SymbolsTable() {
	const {
		data: datasetOptions,
		isLoading: datasetsLoading,
		isError: datasetsError,
	} = useAdminSymbolDatasetsQuery();
	const datasets = datasetOptions ?? EMPTY_DATASETS;
	const [searchParams, setSearchParams] = useSearchParams();
	const urlDatasetIds = React.useMemo(
		() => parsePositiveIdList(searchParams.get(DATASETS_PARAM)),
		[searchParams],
	);
	const [page, setPage] = React.useState(0);
	const [pageSize, setPageSize] = React.useState<AdminSymbolPageSize>(INITIAL_PAGE_SIZE);
	const [columnFilters, setColumnFilters] = React.useState(INITIAL_COLUMN_FILTERS);
	const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
		items: [],
		quickFilterValues: [],
	});
	const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
	const [selectedSymbol, setSelectedSymbol] = React.useState<IAdminSymbol | null>(null);
	const search = getQuickFilterSearch(filterModel);

	const datasetById = React.useMemo(
		() => new Map(datasets.map((dataset) => [dataset.id, dataset])),
		[datasets],
	);
	const selectedDatasets = React.useMemo(
		() => urlDatasetIds.flatMap((datasetId) => {
			const dataset = datasetById.get(datasetId);
			return dataset ? [dataset] : [];
		}),
		[datasetById, urlDatasetIds],
	);
	const dsTypeOptions = React.useMemo(() => {
		const seen = new Set<string>();
		selectedDatasets.forEach((dataset) => {
			dataset.ds_types.forEach((dsType) => {
				if (!seen.has(dsType)) seen.add(dsType);
			});
		});
		return Array.from(seen);
	}, [selectedDatasets]);
	const effectiveDsTypes = React.useMemo(
		() => (datasetOptions === undefined
			? columnFilters.dsTypes
			: columnFilters.dsTypes.filter((dsType) => dsTypeOptions.includes(dsType))),
		[columnFilters.dsTypes, datasetOptions, dsTypeOptions],
	);
	React.useEffect(() => {
		const unchanged = effectiveDsTypes.length === columnFilters.dsTypes.length
			&& effectiveDsTypes.every((dsType, index) => dsType === columnFilters.dsTypes[index]);
		if (datasetOptions === undefined || unchanged) return;
		setColumnFilters((current) => ({ ...current, dsTypes: effectiveDsTypes }));
		setPage(0);
	}, [columnFilters.dsTypes, datasetOptions, effectiveDsTypes]);
	const selectedDatasetIds = React.useMemo(
		() => (datasetOptions === undefined
			? urlDatasetIds
			: selectedDatasets.map((dataset) => dataset.id)),
		[datasetOptions, selectedDatasets, urlDatasetIds],
	);
	const filters = React.useMemo<IAdminSymbolFilters>(() => ({
		...columnFilters,
		dsTypes: effectiveDsTypes,
		datasetIds: selectedDatasetIds,
	}), [columnFilters, effectiveDsTypes, selectedDatasetIds]);
	const hasDatasetSelection = selectedDatasetIds.length > 0;

	React.useEffect(() => {
		const nextParams = new URLSearchParams(searchParams);
		let changed = false;
		if (datasetOptions !== undefined
			&& selectedDatasetIds.join(',') !== urlDatasetIds.join(',')) {
			if (selectedDatasetIds.length > 0) {
				nextParams.set(DATASETS_PARAM, selectedDatasetIds.join(','));
			} else {
				nextParams.delete(DATASETS_PARAM);
			}
			changed = true;
		}
		if (changed) {
			setSearchParams(nextParams, { replace: true });
		}
	}, [
		datasetOptions,
		searchParams,
		selectedDatasetIds,
		setSearchParams,
		urlDatasetIds,
	]);

	const handleOpenSymbol = React.useCallback((symbol: IAdminSymbol) => {
		setSelectedSymbol(symbol);
	}, []);
	const handleFiltersChange = React.useCallback((nextFilters: SymbolColumnFilters) => {
		setColumnFilters(nextFilters);
		setPage(0);
	}, []);
	const handleDatasetChange = React.useCallback((
		nextDatasets: IAdminSymbolDataset[],
	) => {
		const nextParams = new URLSearchParams(searchParams);
		const nextDatasetIds = nextDatasets.map((dataset) => dataset.id);
		if (nextDatasetIds.length > 0) {
			nextParams.set(DATASETS_PARAM, nextDatasetIds.join(','));
		} else {
			nextParams.delete(DATASETS_PARAM);
		}
		setSearchParams(nextParams, { replace: true });
		setPage(0);
	}, [searchParams, setSearchParams]);
	const columns = React.useMemo(() => createColumns({
		onOpen: handleOpenSymbol,
		filters: { ...columnFilters, dsTypes: effectiveDsTypes },
		dsTypeOptions,
		onFiltersChange: handleFiltersChange,
	}), [columnFilters, dsTypeOptions, effectiveDsTypes, handleFiltersChange, handleOpenSymbol]);
	const sort = React.useMemo(() => getSort(sortModel), [sortModel]);
	const { data, isLoading, isFetching, isError } = useAdminSymbolsQuery(
		page,
		pageSize,
		{
			search,
			filters,
			sort,
		},
	);
	const rows = hasDatasetSelection ? data?.items ?? [] : [];

	const handleFilterModelChange = React.useCallback((model: GridFilterModel) => {
		setFilterModel(model);
		setPage(0);
	}, []);
	const handleSortModelChange = React.useCallback((model: GridSortModel) => {
		setSortModel(model);
		setPage(0);
	}, []);
	const handlePageSizeChange = React.useCallback((nextPageSize: number) => {
		if (!isAdminSymbolPageSize(nextPageSize)) return;
		setPageSize(nextPageSize);
		setPage(0);
	}, []);

	const currentView = React.useMemo<SymbolsTableViewState>(() => ({
		search,
		filters,
		sort,
		pageSize,
	}), [filters, pageSize, search, sort]);
	const handleLoadView = React.useCallback((view: SymbolsTableViewState) => {
		const {
			datasetIds,
			...nextColumnFilters
		} = view.filters;
		setFilterModel({
			items: [],
			quickFilterValues: view.search ? [view.search] : [],
		});
		setColumnFilters(nextColumnFilters);
		setSortModel(view.sort ? [{ field: view.sort.field, sort: view.sort.direction }] : []);
		setPageSize(view.pageSize);
		setPage(0);
		const nextParams = new URLSearchParams(searchParams);
		if (datasetIds.length > 0) {
			nextParams.set(DATASETS_PARAM, datasetIds.join(','));
		} else {
			nextParams.delete(DATASETS_PARAM);
		}
		setSearchParams(nextParams, { replace: true });
	}, [searchParams, setSearchParams]);

	return (
		<>
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: {
						xs: 'minmax(0, 1fr)',
						lg: 'minmax(420px, 1fr) minmax(620px, 1.5fr)',
					},
					gap: 2,
					alignItems: 'start',
					mb: 3,
				}}
			>
				<AdminDatasetSelector
					datasets={datasets}
					loading={datasetsLoading}
					error={datasetsError}
					selectedDatasets={selectedDatasets}
					helperText="Select datasets to load symbols."
					onChange={handleDatasetChange}
				/>
				<SymbolsTableViews currentView={currentView} onLoad={handleLoadView} />
			</Box>
			<Box sx={{ height: 'calc(100vh - 360px)', minHeight: 400, width: '100%' }}>
				<DataGrid
					aria-label="Dataset symbols"
					rows={rows}
					columns={columns}
					getRowId={(row) => row.row_id}
					rowCount={hasDatasetSelection ? data?.total ?? 0 : 0}
					loading={hasDatasetSelection && (isLoading || isFetching)}
					error={hasDatasetSelection && isError}
					pagination
					paginationMode="server"
					filterMode="server"
					sortingMode="server"
					page={page}
					localeText={{
						MuiTablePagination: {
							labelDisplayedRows: ({ from, to, count }) => (
								`${from}–${to} of ${formatSymbolTotal(count)}`
							),
						},
					}}
					pageSize={pageSize}
					rowsPerPageOptions={[...ADMIN_SYMBOL_PAGE_SIZE_OPTIONS]}
					onPageChange={setPage}
					onPageSizeChange={handlePageSizeChange}
					filterModel={filterModel}
					onFilterModelChange={handleFilterModelChange}
					sortModel={sortModel}
					onSortModelChange={handleSortModelChange}
					disableColumnFilter
					disableSelectionOnClick
					density="compact"
					components={{
						Toolbar: AdminQuickFilterToolbar,
						NoRowsOverlay: hasDatasetSelection
							? SymbolsNoRowsOverlay
							: SymbolsDatasetSelectionOverlay,
						ErrorOverlay: SymbolsErrorOverlay,
					}}
					componentsProps={{
						toolbar: {
							enableExport: false,
							quickFilterParser: parseSymbolSearch,
						},
					}}
					sx={{
						'& .MuiDataGrid-cell': {
							minWidth: 0,
						},
					}}
				/>
			</Box>
			<SymbolDetailsDialog
				open={selectedSymbol !== null}
				symbols={selectedSymbol ? [selectedSymbol] : []}
				onClose={() => setSelectedSymbol(null)}
			/>
		</>
	);
}
